import React from "react";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import { Phone, Mail, Building2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import SEO from "@/components/SEO";

// Approximate latitude/longitude for our three office cities, converted to
// percentage coordinates inside the SVG India outline (viewBox 0 0 500 600).
// Pin positions are tuned to roughly match the India map silhouette below.
const OFFICES = [
  {
    label: "Corporate Office",
    city: "Noida",
    address: "UNIT NO B1211, 12TH FLOOR, TOWER T4, NX-1, Plot No 17 Tech Zone IV, Greater Noida (West), Uttar Pradesh",
    accent: "bg-emerald-100 text-emerald-700",
    mapX: 263, mapY: 200,
  },
  {
    label: "Branch Office",
    city: "Raipur",
    address: "226, Regus, Second Floor, Block-B, Shree Ram Business Park, Vidhan Sabha Road, Raipur",
    accent: "bg-sky-100 text-sky-700",
    mapX: 305, mapY: 320,
  },
  {
    label: "Registered Office",
    city: "Patna",
    address: "HN-502/3A, CN-50B, AT-JAKARIYAPUR, PO-BARI PAHARI, 2nd Street East of Bajrangi Chowk, Patna-800007",
    accent: "bg-amber-100 text-amber-700",
    mapX: 345, mapY: 245,
  },
];

export default function Contact() {
  const { t } = useI18n();
  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Reach NakshaCart's Patna, Raipur or Noida offices by phone, email or the contact form."
        path="/contact"
      />
      <div className="bg-gradient-to-b from-emerald-50/40 to-white border-b border-slate-200">
        <div className="nc-container py-12 md:py-16">
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">{t("contact.title")}</h1>
          <p className="mt-3 text-slate-600 max-w-3xl">
            Our presence is in three important cities of India — <strong>Patna</strong>, <strong>Raipur</strong>, and <strong>Noida</strong> — from where we provide services across India.
          </p>
        </div>
      </div>

      <div className="nc-container py-10 grid lg:grid-cols-3 gap-8">
        <div className="space-y-4">
          <ContactItem icon={Phone} title={t("contact.phone")} value="+91 77798 94479" />
          <ContactItem icon={Mail} title={t("contact.email")} value="hello@nakshacart.com" />
        </div>
        <div className="lg:col-span-2">
          <LeadCaptureForm source="contact" dataTestidPrefix="contact" />
        </div>
      </div>

      <div className="nc-container pb-16">
        <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Our offices</h2>
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          {OFFICES.map((o) => (
            <div key={o.label} className="bg-white rounded-2xl p-5 border border-slate-200 nc-soft-shadow" data-testid={`office-${o.city.toLowerCase()}`}>
              <div className={`w-10 h-10 rounded-xl ${o.accent} flex items-center justify-center mb-3`}>
                <Building2 className="w-5 h-5" />
              </div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{o.label}</div>
              <div className="mt-1 text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{o.city}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{o.address}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5 nc-soft-shadow">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Locations on the India map</h3>
          <div className="relative w-full max-w-2xl mx-auto">
            <svg viewBox="0 0 500 600" className="w-full h-auto" aria-label="India map with office locations" data-testid="contact-india-map">
              {/* Simplified India outline. Not geographically precise — meant as a friendly visual cue. */}
              <path
                d="M 215 80 L 240 70 L 270 80 L 295 75 L 315 95 L 325 120 L 320 145 L 350 150 L 370 175 L 365 205 L 355 225 L 345 235 L 350 260 L 360 290 L 355 320 L 340 345 L 320 360 L 305 380 L 295 410 L 285 440 L 270 465 L 250 480 L 235 500 L 220 515 L 205 525 L 195 510 L 185 480 L 180 450 L 175 420 L 165 390 L 155 360 L 145 335 L 135 305 L 130 275 L 135 245 L 145 220 L 155 200 L 160 175 L 155 150 L 165 125 L 185 100 Z"
                fill="#ecfdf5"
                stroke="#10b981"
                strokeWidth="2"
              />
              {OFFICES.map((o) => (
                <g key={o.city}>
                  <circle cx={o.mapX} cy={o.mapY} r="22" fill="rgba(16,185,129,0.15)" />
                  <circle cx={o.mapX} cy={o.mapY} r="8" fill="#10b981" stroke="white" strokeWidth="3" />
                  <text x={o.mapX} y={o.mapY - 16} textAnchor="middle" fontSize="13" fontWeight="600" fill="#065f46">
                    {o.city}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactItem({ icon: Icon, title, value }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200 flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</div>
        <div className="font-medium text-slate-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}
